import { useState, useRef, useCallback } from 'react';
import { useI18n } from '../lib/i18n';

/**
 * Cuerpo de email LEGIBLE:
 * - Email HTML "de verdad" (con <style>/<head>/tablas de Outlook, ej. Allstate) → se renderiza
 *   en un IFRAME AISLADO (sandbox, sin scripts) para que se vea COMO UN EMAIL NORMAL, con su
 *   diseño/branding. Antes se sanitizaba y quedaba el CSS del <style> como texto crudo a la vista.
 * - Email HTML SIMPLE (nuestro editor: negritas/GIFs/links) → inline sanitizado (liviano).
 * - Email de TEXTO PLANO → saltos de línea respetados; las líneas citadas (">", ">>") pierden los
 *   marcadores y se indentan (estilo Gmail); la cadena citada se recoge tras "⋯ Ver texto citado".
 */

/** ¿Es un email HTML COMPLETO (con estilos/estructura de email real)? → se muestra en iframe. */
const isFullHtmlEmail = (s: string) =>
  /<\s*style[\s>]|<\/\s*style>|<\s*html[\s>]|<\s*head[\s>]|<!doctype\s+html|<\s*table[\s>][\s\S]*<\s*td[\s>]|mso-/i.test(s);

/** Documento del iframe: quita <script>, pone target=_blank en los links y un estilo base legible.
 *  El <style> propio del email SE CONSERVA (es lo que lo hace verse bien). */
function buildEmailSrcDoc(html: string): string {
  const noScript = acortarAnchorsGigantes(html.replace(/<\s*script[\s\S]*?<\/\s*script\s*>/gi, ''));
  /**
   * EL `!important` NO ES DECORACIÓN.
   *
   * Reporte de Manuel (2026-08-25): "donde te muestro la foto se ve muy grande para el espacio que
   * tiene". La regla `img{max-width:100%}` ya estaba desde antes y no alcanzaba: los correos de
   * marketing (el de JobNimbus del reporte, y todos los de las aseguradoras) traen el ancho EN
   * LÍNEA —`<img style="width:600px">`, `<table width="600">`— y un estilo en línea le gana a la
   * hoja de estilos. Sin `!important` la regla estaba escrita pero no se aplicaba nunca.
   *
   * Va también sobre `table`, que es de lo que están hechos esos correos: con la imagen acotada
   * pero la tabla en 600px fijos, el cuerpo sigue siendo más ancho que el panel.
   *
   * Y `overflow-x:auto` como red: si algo se resiste igual (un ancho en un elemento que no
   * cubrimos), scrollea DENTRO del correo en vez de deformar la pantalla.
   */
  const head = '<base target="_blank" rel="noopener noreferrer"><meta charset="utf-8">'
    + '<style>html,body{margin:0;padding:8px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#111;background:#fff;word-break:break-word}'
    + 'body{overflow-x:auto}'
    + 'img{max-width:100%!important;height:auto!important}'
    + 'table,td{max-width:100%!important}'
    + 'a{color:#2563eb}</style>';
  if (/<head[^>]*>/i.test(noScript)) return noScript.replace(/<head[^>]*>/i, (m) => m + head);
  if (/<html[^>]*>/i.test(noScript)) return noScript.replace(/<html[^>]*>/i, (m) => `${m}<head>${head}</head>`);
  return `<!doctype html><html><head>${head}</head><body>${noScript}</body></html>`;
}

/** Email HTML real en un iframe aislado (sandbox SIN scripts → seguro; allow-same-origin solo para
 *  medir el alto). Auto-ajusta la altura al contenido; re-mide tras cargar imágenes. */
function EmailHtmlFrame({ html, style }: { html: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const resize = useCallback(() => {
    const doc = ref.current?.contentDocument;
    if (doc?.body) ref.current!.style.height = `${Math.min(doc.body.scrollHeight + 4, 6000)}px`;
  }, []);
  const onLoad = useCallback(() => { resize(); setTimeout(resize, 250); setTimeout(resize, 900); }, [resize]);
  return (
    <iframe
      ref={ref}
      title="email"
      srcDoc={buildEmailSrcDoc(html)}
      onLoad={onLoad}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', border: 0, height: 220, background: '#fff', borderRadius: 8, ...style }}
    />
  );
}

const sanitize = (h: string) => h
  .replace(/<(?!\/?(br|b|strong|i|em|u|s|strike|a|p|div|span|ul|ol|li|img)\b)[^>]*>/gi, '')
  .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
  .replace(/(href|src)\s*=\s*(["']?)\s*javascript:[^"'\s>]*\2/gi, '');

const escapeHtml = (s: string) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** URLs sueltas → links clickeables (fuera de <a> existentes).
 *  OJO (bug de los links de Drive, 2026-07-28): el texto llega HTML-ESCAPADO, así que los
 *  `<url>` estilo RFC (Gmail los manda así) aparecen como `&lt;url&gt;` — y `&gt;` pasaba el
 *  filtro de caracteres, dejando el href como `…CMW&gt` → Drive rechazaba el archivo. La URL
 *  se corta en la primera entidad `&lt;`/`&gt;`/`&quot;` (eran < > " literales = delimitadores)
 *  y después se recorta la puntuación final (`;`, `.`, `)`, …). */
/** Texto VISIBLE de un link cuando es una URL kilométrica (reporte 2026-08-13, CompanyCam pone
 *  la URL prefirmada de S3 entera como texto del <a>): host + inicio del path + "…". El href
 *  queda INTACTO — solo se acorta lo que se ve. */
const shortUrlText = (url: string): string => {
  if (url.length <= 90) return url;
  try {
    const u = new URL(url);
    const path = u.pathname.length > 24 ? `${u.pathname.slice(0, 24)}…` : u.pathname;
    return `${u.host}${path}`;
  } catch { return `${url.slice(0, 80)}…`; }
};

/** En HTML ya armado: si el TEXTO de un <a> es una URL gigante, se muestra acortada. */
const acortarAnchorsGigantes = (html: string): string =>
  html.replace(/(<a\b[^>]*>)\s*(https?:\/\/[^<\s]{90,})\s*(<\/a>)/gi,
    (_m, open: string, url: string, close: string) => `${open}${shortUrlText(url)}${close}`);

const linkify = (html: string): string => {
  const parts = html.split(/(<[^>]*>)/g);
  let inA = 0;
  return parts.map((part) => {
    if (part.startsWith('<')) {
      if (/^<a[\s>]/i.test(part)) inA++;
      else if (/^<\/a>/i.test(part)) inA = Math.max(0, inA - 1);
      return part;
    }
    if (inA > 0 || !part) return part;
    return part.replace(/https?:\/\/[^\s<>"']+/g, (raw) => {
      const cut = raw.search(/&(lt|gt|quot);/);
      let url = cut >= 0 ? raw.slice(0, cut) : raw;
      let rest = raw.slice(url.length);
      url = url.replace(/[.,;:!?)\]]+$/, (m) => { rest = m + rest; return ''; });
      if (!url) return raw;
      return `<a href="${url}" target="_blank" rel="noopener" style="color:var(--info);text-decoration:underline;word-break:break-all">${shortUrlText(url)}</a>${rest}`;
    });
  }).join('');
};

/** ¿El body es HTML de verdad (tags) o texto plano con < > sueltos? */
export const looksLikeHtml = (s: string) =>
  /<(br|b|strong|i|em|u|s|strike|a|p|div|span|ul|ol|li|img|table|td|tr|h[1-6])\b[^>]*>/i.test(s);

/** Separa el mensaje propio de la cadena citada (líneas ">", "On … wrote:"). Si el email ES
 *  la cita (forward sin texto propio), no separa nada — se muestra todo formateado. */
function splitQuoted(text: string): { main: string; quoted: string | null } {
  const lines = text.split(/\r?\n/);
  let idx = -1;
  for (let i = 0; i < lines.length; i++) {
    const l = (lines[i] ?? '').trim();
    if (/^>/.test(l) || /^On .{0,140} wrote:?$/.test(l) || /^El .{0,140} escribi(ó|o):?$/.test(l)) { idx = i; break; }
  }
  if (idx < 0) return { main: text, quoted: null };
  const main = lines.slice(0, idx).join('\n').trim();
  if (main.replace(/\s+/g, '').length < 40) return { main: text, quoted: null };
  return { main, quoted: lines.slice(idx).join('\n').trim() };
}

/** Render de texto plano: agrupa líneas por nivel de cita; los ">" se quitan y se ven como
 *  bloques indentados con barra lateral. Los saltos de línea se respetan. */
function PlainBlocks({ text }: { text: string }) {
  const groups: Array<{ depth: number; lines: string[] }> = [];
  for (const raw of text.split(/\r?\n/)) {
    const m = /^((?:\s*>)+)\s?/.exec(raw);
    const depth = m ? (m[1]!.match(/>/g) || []).length : 0;
    const clean = m ? raw.slice(m[0].length) : raw;
    const last = groups[groups.length - 1];
    if (last && last.depth === depth) last.lines.push(clean);
    else groups.push({ depth, lines: [clean] });
  }
  return (
    <>
      {groups.map((g, i) => (
        <div
          key={i}
          style={g.depth === 0
            ? { whiteSpace: 'pre-wrap' }
            : { whiteSpace: 'pre-wrap', color: 'var(--muted)', borderLeft: '3px solid var(--line)', paddingLeft: 10, marginLeft: Math.min(g.depth - 1, 4) * 10, margin: '2px 0' }}
          dangerouslySetInnerHTML={{ __html: linkify(escapeHtml(g.lines.join('\n'))) }}
        />
      ))}
    </>
  );
}

export function EmailBody({ body, style }: { body: string | null | undefined; style?: React.CSSProperties }) {
  const { t } = useI18n();
  const [showQuoted, setShowQuoted] = useState(false);
  if (!body || !body.trim()) return null;
  // Email HTML COMPLETO (Allstate, etc.) → iframe aislado, se ve como email normal.
  if (isFullHtmlEmail(body)) {
    return <EmailHtmlFrame html={body} style={style} />;
  }
  // Email HTML simple (nuestro editor) → inline sanitizado.
  if (looksLikeHtml(body)) {
    return <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, ...style }} dangerouslySetInnerHTML={{ __html: linkify(sanitize(body)) }} />;
  }
  const { main, quoted } = splitQuoted(body);
  return (
    <div style={{ lineHeight: 1.55, ...style }}>
      <PlainBlocks text={main} />
      {quoted && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button" className="btn ghost sm"
            style={{ fontSize: 'var(--fs-2xs)', padding: '1px 8px', background: 'var(--surface-2)', borderRadius: 10 }}
            onClick={() => setShowQuoted((v) => !v)}
          >
            {showQuoted ? `▾ ${t('em.hideQuoted')}` : `⋯ ${t('em.showQuoted')}`}
          </button>
          {showQuoted && <div style={{ marginTop: 6 }}><PlainBlocks text={quoted} /></div>}
        </div>
      )}
    </div>
  );
}
